import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { TOPICS, ARTICLES } from "@/data/blog";

const BlogPreview = () => {
  const { lang, t } = useLanguage();
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/latest`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d?.posts?.length) setRemote(d.posts);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const latest = remote
    ? remote.map((p) => ({
        year: String(new Date(p.published_at).getFullYear()),
        url: p.url,
        title: { it: p.title, en: p.title },
      }))
    : ARTICLES.slice(0, 3);

  return (
    <section className="border-t border-white/10 px-6 py-24 md:px-12" data-testid="blog-preview">
      <div className="mx-auto max-w-7xl">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-neon">
              <BookOpen className="h-4 w-4" />
              {t.blogPrev.kicker}
            </p>
            <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl" data-testid="blog-preview-title">
              {t.blogPrev.title}
            </h2>
          </div>
          <Link
            to="/oltre"
            data-testid="blog-preview-more"
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-neon hover:text-neon"
          >
            {t.blogPrev.more}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {latest.map((a, i) => (
            <Reveal key={a.url} delay={i * 0.1}>
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`blog-preview-article-${i}`}
                className="group flex h-full flex-col border border-white/10 bg-surface p-8 transition-transform duration-300 hover:-translate-y-1.5"
              >
                <div className="flex items-center gap-3 font-mono text-xs text-muted2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TOPICS[a.tag]?.color || "#9146FF" }} />
                  {a.year}
                </div>
                <h3 className="mt-4 font-display text-xl font-bold leading-snug tracking-tight">{a.title[lang]}</h3>
                {a.excerpt && <p className="mt-3 flex-1 text-sm leading-relaxed text-muted2">{a.excerpt[lang]}</p>}
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 transition-colors duration-300 group-hover:text-neon">
                  {t.beyond.readArticle}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
